/** @format — Run one AG-UI stream turn and drive message state updates */

import {
  streamAguiRun,
  createAguiIds,
  buildStreamPayload,
  buildUserStreamMessage,
  buildStreamState,
} from "./aguiStream";
import { hasKnownAguiComponents } from "./aguiMessageUtils";
import { resolveAgentUiForDisplay, stripCarriedOverTurnUi } from "./aguiDisplayUtils";
import { filterKnownUiComponents } from "./normalizeComponents";
import { openPaymentFromStreamEvent } from "./openPaymentFromStream";
import {
  isPaymentMetadataDelta,
  sanitizeStreamText,
} from "./sanitizeStreamText";
import {
  createThrottledTextPatch,
  isAguiSignalTool,
  isHiddenStreamTool,
} from "./streamingUtils";

/**
 * @param {object} params
 * @param {function} params.onPatch — (patch) => void updates streaming agent message
 * @param {function} [params.onPaymentEvent] — (paymentEvent) => void
 * @param {object} [params.user] — for payment modal payload
 */
export async function runAguiStreamTurn({
  threadId,
  isNewSession,
  userId,
  userContent,
  forwardedProps = {},
  previousTurnUi = [],
  timeZone,
  offsetMinutes,
  onPatch,
  onPaymentEvent,
  user,
  signal,
}) {
  const { runId, messageId } = createAguiIds();

  const messages = userContent
    ? [buildUserStreamMessage(userContent, userId, messageId)]
    : [];

  const payload = buildStreamPayload({
    threadId,
    runId,
    messages,
    state: buildStreamState({
      sessionId: threadId,
      chatId: threadId,
      newSession: isNewSession,
      timeZone,
      offsetMinutes,
    }),
    forwardedProps,
  });

  const streamTools = [];
  const textPatch = createThrottledTextPatch((patch) => {
    if (patch.text != null) {
      onPatch({ ...patch, text: sanitizeStreamText(patch.text) });
    } else {
      onPatch(patch);
    }
  });

  const isActionTurn = Boolean(forwardedProps?.action);
  const carriedUi = filterKnownUiComponents(previousTurnUi);
  let isAguiTurn = isActionTurn;
  let turnUi = [];
  let paymentOpened = false;
  let paymentDisplayText = null;
  let skipStreamTextDeltas = false;

  const handlePaymentEvent = (event) => {
    if (!event || paymentOpened) return;
    paymentOpened = true;
    onPaymentEvent?.(event);
    openPaymentFromStreamEvent(event, user);
  };

  const result = await streamAguiRun(
    payload,
    {
      onRunStarted: () => {
        onPatch({
          uiComponents: [],
          streamTools: [],
          streamStatus: "thinking",
          isAgui: isAguiTurn,
          text: "",
        });
      },
      onTextDelta: (text, delta) => {
        if (skipStreamTextDeltas || isPaymentMetadataDelta(delta)) {
          return;
        }
        textPatch.push(text);
      },
      onUiComponents: (uiComponents, meta = {}) => {
        if (meta.source === "snapshot") return;
        const userPrompt = isActionTurn
          ? String(
              forwardedProps?.action?.label ??
                forwardedProps?.action?.action_id ??
                "",
            )
          : (userContent ?? "");
        turnUi = stripCarriedOverTurnUi(uiComponents ?? [], carriedUi, userPrompt);
        if (hasKnownAguiComponents(turnUi)) {
          isAguiTurn = true;
        }
        onPatch({
          uiComponents: turnUi,
          isAgui: hasKnownAguiComponents(turnUi),
        });
      },
      onToolStart: (ev) => {
        const name = ev.toolCallName ?? "working";
        const id = ev.toolCallId ?? name;

        if (isAguiSignalTool(name)) {
          isAguiTurn = true;
        }

        if (isHiddenStreamTool(name)) {
          onPatch({ streamStatus: "tool", isAgui: isAguiTurn });
          return;
        }

        if (!isAguiTurn) return;

        const existing = streamTools.find((t) => t.id === id);
        if (!existing) {
          streamTools.push({ id, name, status: "running" });
        }
        onPatch({
          streamStatus: "tool",
          streamTool: name,
          streamTools: [...streamTools],
          isAgui: true,
        });
      },
      onToolEnd: (ev) => {
        const name = ev.toolCallName;
        if (isHiddenStreamTool(name)) {
          onPatch({
            streamStatus: streamTools.some((x) => x.status === "running")
              ? "tool"
              : "writing",
            isAgui: isAguiTurn,
          });
          return;
        }
        if (!isAguiTurn) return;

        const id = ev.toolCallId;
        const t = streamTools.find((x) => x.id === id);
        if (t) t.status = "done";
        const running = streamTools.find((x) => x.status === "running");
        onPatch({
          streamStatus: running ? "tool" : "writing",
          streamTool: running?.name ?? null,
          streamTools: [...streamTools],
        });
      },
      onToolResult: (_ev, parsed) => {
        if (!parsed) return;

        if (parsed.paymentEvent) {
          isAguiTurn = true;
          skipStreamTextDeltas = true;
          handlePaymentEvent(parsed.paymentEvent);
        }

        if (parsed.displayMessage) {
          paymentDisplayText = parsed.displayMessage;
          textPatch.cancel();
          onPatch({
            text: parsed.displayMessage,
            streamStatus: "writing",
            isAgui: true,
            streamShowText: true,
          });
        }
      },
      onTextStart: () => onPatch({ streamStatus: "writing" }),
      onPaymentEvent: handlePaymentEvent,
      onRunFinished: ({
        text: finishedText,
        uiComponents: finishedUi,
        paymentEvent,
      }) => {
        textPatch.cancel();
        const rawText =
          paymentDisplayText || finishedText || textPatch.flush() || "";
        const actionLabel =
          forwardedProps?.action?.label ??
          forwardedProps?.action?.action_id ??
          "";
        const userPrompt = isActionTurn ? String(actionLabel) : (userContent ?? "");
        const uiSource = turnUi.length ? turnUi : stripCarriedOverTurnUi(finishedUi ?? [], carriedUi, userPrompt);
        const resolvedUi = resolveAgentUiForDisplay({
          text: sanitizeStreamText(rawText),
          uiComponents: uiSource,
          userPrompt,
          isActionFollowUp: isActionTurn,
          previousTurnUi: carriedUi,
        });
        onPatch({
          text: sanitizeStreamText(rawText),
          uiComponents: resolvedUi.uiComponents,
          isAgui: resolvedUi.isAgui,
          isStreaming: false,
          streamStatus: null,
          streamTool: null,
          streamTools: [],
        });
        if (paymentEvent) {
          handlePaymentEvent(paymentEvent);
        }
      },
      onRunError: (event) => {
        textPatch.cancel();
        const code = event?.code;
        const text =
          code === "VOICE_SESSION_ACTIVE"
            ? "A voice session is active on this chat. End it to continue by text."
            : code === "THREAD_TURN_ACTIVE"
              ? "Another turn is already running on this chat. Wait for it to finish."
              : "Sorry, I hit a snag processing that. Please try again in a moment.";
        onPatch({
          isStreaming: false,
          streamStatus: null,
          streamTools: [],
          text,
        });
      },
    },
    signal,
  );

  return { ...result, threadId: result.threadId ?? threadId };
}
